import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { KeyReturn, Copy, Trash, Plus, UserCirclePlus, Buildings } from "@phosphor-icons/react";

/**
 * B-009 · Tarjetas de configuración para el flujo Gestor Multi-Centro (AEAT).
 *
 * - GestorCodesCard: visible sólo para `role=gestor`. Permite generar hasta 5
 *   códigos secundarios para compartir con admins de OTROS talleres y darse de
 *   alta como gestor en sus paneles sin crear cuentas extra.
 *
 * - RedeemGestorCodeCard: visible para admin/owner/superadmin. Permite canjear
 *   el código que un gestor externo le ha facilitado para que pueda acceder a
 *   esta organización.
 */
export function GestorMultiOrgCards({ role }) {
  return (
    <div className="space-y-6 mt-6" data-testid="gestor-multi-cards">
      {role === "gestor" && <GestorCodesCard />}
      {role === "gestor" && <GestorClientCentersCard />}
    </div>
  );
}

// Tarjeta usada en el Dashboard del centro para que el admin invite a su gestor
export function InvitarMiGestorCard() {
  return <RedeemGestorCodeCard />;
}

// ---------------- Códigos secundarios (gestor) ----------------
function GestorCodesCard() {
  const [data, setData] = useState({ items: [], active: 0, max: 5 });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/me/gestor-codes"); setData(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post("/me/gestor-codes", { note });
      setNote("");
      toast.success("Código generado. Cópialo y compártelo con el admin del centro.");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const revoke = async (code) => {
    if (!window.confirm(`¿Revocar el código ${code}? El admin ya no podrá canjearlo.`)) return;
    try { await api.delete(`/me/gestor-codes/${code}`); toast.success("Código revocado"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`Copiado: ${txt}`); }
    catch { /* ignore */ }
  };

  return (
    <section className="ag-card p-6" data-testid="gestor-codes-card">
      <div className="flex items-center gap-2 mb-2">
        <KeyReturn size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-lg font-bold tracking-tighter">Códigos secundarios · Gestor multi-centro</h3>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-4 leading-relaxed">
        Genera un código por cada cliente al que asesoras. Cuando el admin de ese centro lo canjee en su panel,
        accederás a su organización con tu mismo perfil (sin crear cuentas nuevas). Máximo <strong className="text-[var(--ag-text)]">{data.max}</strong> códigos activos a la vez.
      </p>

      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mb-5">
        <Input
          value={note} onChange={(e)=>setNote(e.target.value)}
          placeholder="Nota interna (ej: Taller Pérez SL)"
          className="h-10" maxLength={200}
          data-testid="gestor-code-note"
          disabled={busy || data.active >= data.max}
        />
        <Button type="submit" className="ag-btn-gold h-10" disabled={busy || data.active >= data.max} data-testid="gestor-code-create">
          <Plus size={14} className="mr-1.5" /> Generar código
        </Button>
      </form>

      {data.active >= data.max && (
        <div className="mb-4 p-3 rounded-sm border border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-200" data-testid="gestor-code-limit-warning">
          Has alcanzado el máximo de {data.max} códigos activos. Revoca alguno para generar uno nuevo.
        </div>
      )}

      <div className="overflow-x-auto -mx-1" data-testid="gestor-code-list">
        {data.items.length === 0 ? (
          <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center border border-dashed border-[var(--ag-border)] rounded-sm">
            Aún no has generado ningún código.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
              <tr>
                <th className="text-left px-3 py-2">Código</th>
                <th className="text-left px-3 py-2">Nota</th>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">Canjeado por</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => {
                const status = c.revoked_at ? "revoked"
                              : c.used_at ? "used"
                              : "active";
                return (
                  <tr key={c.code} className="border-t border-[var(--ag-border)] text-[var(--ag-text)]" data-testid={`gestor-code-row-${c.code}`}>
                    <td className="px-3 py-2 font-mono text-[11px] tracking-wider">{c.code}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate text-[var(--ag-text-dim)]">{c.note || "—"}</td>
                    <td className="px-3 py-2">
                      {status === "active" && <span className="text-emerald-400 text-[10px] uppercase font-bold">Activo</span>}
                      {status === "used"    && <span className="text-[var(--ag-gold)] text-[10px] uppercase font-bold">Canjeado</span>}
                      {status === "revoked" && <span className="text-red-400 text-[10px] uppercase font-bold">Revocado</span>}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-[var(--ag-text-dim)]">{c.used_by_org_name || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap space-x-1 text-right">
                      {status === "active" && (
                        <>
                          <button onClick={()=>copy(c.code)} className="px-2 py-1 rounded-sm border border-[var(--ag-border)] text-[10px] uppercase hover:bg-[var(--ag-midnight-soft)]" data-testid={`gestor-code-copy-${c.code}`}>
                            <Copy size={11} className="inline mr-1" /> Copiar
                          </button>
                          <button onClick={()=>revoke(c.code)} className="text-red-400 hover:text-red-300 p-1" title="Revocar" data-testid={`gestor-code-revoke-${c.code}`}>
                            <Trash size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ---------------- Mis centros como gestor (vista cliente-by-cliente) ----------------
function GestorClientCentersCard() {
  const [orgs, setOrgs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [primaryId, setPrimaryId] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/me/orgs"); setOrgs(data.items || []); setActiveId(data.active_org_id); setPrimaryId(data.primary_org_id); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const goTo = async (org_id) => {
    if (org_id === activeId) { window.location.href = "/dashboard"; return; }
    setBusy(org_id);
    try {
      await api.post("/auth/switch-org", { org_id });
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No se pudo entrar");
      setBusy(null);
    }
  };
  const clients = orgs.filter((o) => o.id !== primaryId); // excluye org propia del gestor
  return (
    <section className="ag-card p-6" data-testid="gestor-client-centers">
      <div className="flex items-center gap-2 mb-2">
        <Buildings size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-lg font-bold tracking-tighter">Mis centros como gestor</h3>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-4 leading-relaxed">
        Cada centro que ha canjeado tu código aparece aquí. Pulsa <strong className="text-[var(--ag-text)]">Acceder</strong> para entrar a su panel y gestionar su contabilidad sin mezclar datos con los demás.
      </p>
      {clients.length === 0 ? (
        <div className="p-6 text-sm text-[var(--ag-text-dim)] text-center border border-dashed border-[var(--ag-border)] rounded-sm">
          Aún ningún centro ha canjeado tus códigos. Comparte un código con el admin del centro y aparecerá aquí.
        </div>
      ) : (
        <div className="space-y-2">
          {clients.map((o) => (
            <div key={o.id} className="flex items-center gap-3 p-3 rounded-sm border border-[var(--ag-border)] bg-[var(--ag-midnight-soft)]" data-testid={`gestor-client-${o.id}`}>
              {o.logo_url ? (
                <img src={o.logo_url} alt="" className="w-10 h-10 rounded-sm object-cover shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] flex items-center justify-center shrink-0">
                  <Buildings size={18} weight="duotone" className="text-[var(--ag-gold)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[var(--ag-text)] truncate">{o.name}</div>
                <div className="text-[10px] text-[var(--ag-text-dim)]">{o.city || "—"}</div>
              </div>
              <Button size="sm" onClick={() => goTo(o.id)} disabled={busy === o.id}
                className={`h-9 text-xs uppercase tracking-wider ${o.id === activeId ? "ag-btn-gold" : "border border-[var(--ag-gold)]/40 text-[var(--ag-gold)] bg-transparent hover:bg-[var(--ag-gold)]/10"}`}
                data-testid={`gestor-client-go-${o.id}`}>
                {busy === o.id ? "Entrando…" : (o.id === activeId ? "Activo · Abrir" : "Acceder")}
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------- Canjear código (admin / superadmin) ----------------
function RedeemGestorCodeCard() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const redeem = async (e) => {
    e?.preventDefault?.();
    const c = code.trim().toUpperCase();
    if (!c) { toast.error("Introduce un código válido"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orgs/redeem-gestor-code", { code: c });
      setLastResult(data);
      setCode("");
      toast.success(`${data.gestor.name} ya tiene acceso a tu centro como gestor`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="ag-card p-6" data-testid="redeem-gestor-card">
      <div className="flex items-center gap-2 mb-2">
        <UserCirclePlus size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <h3 className="font-display text-lg font-bold tracking-tighter">Invitar a mi gestor</h3>
      </div>
      <p className="text-xs text-[var(--ag-text-dim)] mb-4 leading-relaxed">
        ¿Tu asesor fiscal usa AutoGlow para llevar otros centros? Pídele que te envíe un <strong className="text-[var(--ag-text)]">código secundario</strong> y
        canjéalo aquí. Tendrá acceso de sólo lectura a tu dashboard, gastos y declaraciones AEAT sin crear un perfil nuevo en tu centro.
      </p>

      <form onSubmit={redeem} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
        <Input
          value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())}
          placeholder="GS-XXXXXXXX"
          className="h-10 font-mono tracking-wider"
          maxLength={20}
          data-testid="redeem-gestor-input"
        />
        <Button type="submit" className="ag-btn-gold h-10" disabled={busy} data-testid="redeem-gestor-submit">
          {busy ? "Canjeando…" : "Canjear código"}
        </Button>
      </form>

      {lastResult && (
        <div className="mt-4 p-3 rounded-sm border border-emerald-500/30 bg-emerald-500/10" data-testid="redeem-gestor-success">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1 flex items-center gap-1.5">
            <Buildings size={11} weight="duotone" /> Gestor añadido
          </div>
          <div className="text-sm text-[var(--ag-text)]">
            <strong>{lastResult.gestor.name}</strong> · {lastResult.gestor.email}
          </div>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-1">
            Ahora podrá ver y trabajar con tu centro <em>{lastResult.org_name}</em> desde su propio panel.
          </div>
        </div>
      )}
    </section>
  );
}
